import type { ComponentType, CSSProperties, ReactNode, SVGProps } from 'react';
import { Fragment } from 'react';
import classNames from 'classnames';
import { Check, Layers, Package, Puzzle, Rocket, Sparkles, TriangleAlert } from 'lucide-react';
import type {
  CatalogArtifact,
  CatalogArtifactEntity,
  CatalogArtifactMatrix,
  CatalogArtifactState,
} from '../../agent/agent-api';
import styles from './CatalogArtifactCard.module.css';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const ENTITY: Record<
  CatalogArtifactEntity,
  { icon: IconComponent; kicker: string; cls: string }
> = {
  bundle: { icon: Package, kicker: 'Bundle', cls: styles.Bundle },
  module: { icon: Layers, kicker: 'Module', cls: styles.Module },
  component: { icon: Puzzle, kicker: 'Component', cls: styles.Component },
};

const STATE: Record<CatalogArtifactState, { icon: IconComponent; label: string; cls: string }> = {
  draft: { icon: Sparkles, label: 'Draft · needs review', cls: styles.StateDraft },
  confirmed: { icon: Check, label: 'Added to catalog', cls: styles.StateConfirmed },
  attention: { icon: TriangleAlert, label: 'Needs attention', cls: styles.StateAttention },
  published: { icon: Rocket, label: 'Published · live', cls: styles.StatePublished },
};

function PriceMatrix({ matrix }: { matrix: CatalogArtifactMatrix }) {
  // Column count is runtime data → expose it as a CSS custom property (the
  // sanctioned inline-style exception); the module's CSS builds the template.
  return (
    <div
      className={styles.Matrix}
      style={{ '--rv-matrix-columns': matrix.columns.length } as CSSProperties}
    >
      <span className={styles.MatrixCorner} />
      {matrix.columns.map((column, index) => (
        <span key={index} className={styles.MatrixHead}>
          {column}
        </span>
      ))}
      {matrix.rows.map((row, rowIndex) => (
        <Fragment key={rowIndex}>
          <span className={styles.MatrixRowLabel}>{row.label}</span>
          {row.cells.map((cell, cellIndex) => (
            <span
              key={cellIndex}
              className={classNames(styles.MatrixCell, { [styles.MatrixCellMissing]: cell.missing })}
            >
              <b>{cell.value}</b>
              {cell.sub && <em>{cell.sub}</em>}
            </span>
          ))}
        </Fragment>
      ))}
    </div>
  );
}

export interface CatalogArtifactCardProps {
  artifact: CatalogArtifact;
  /** Action bar content (e.g. Approve / Decline, or a status line). */
  footer?: ReactNode;
  /** Optional "additions" section rendered at the end of the body (before the footer). */
  additions?: ReactNode;
}

/**
 * "Spec sheet" card for a catalog entity (bundle / module / variable component)
 * the agent proposes to create — a banded, entity-tinted header plus a spec body
 * (facts, module refs, attached components, a period × market price grid, and
 * publish gaps). Ported from the Chat Artifact Cards design (Direction B) to the
 * app's --rv-* tokens; renders the same in light and dark. The confirm flow is
 * supplied via `footer`.
 */
export function CatalogArtifactCard({ artifact, footer, additions }: CatalogArtifactCardProps) {
  const entity = ENTITY[artifact.entity];
  const state = STATE[artifact.state ?? 'draft'];
  const EntityIcon = entity.icon;
  const StateIcon = state.icon;

  return (
    <div className={classNames(styles.Card, entity.cls, styles[`S${artifact.state ?? 'draft'}`])}>
      <div className={styles.Band}>
        <span className={styles.BandIcon}>
          <EntityIcon aria-hidden="true" />
        </span>
        <div className={styles.BandTitles}>
          <div className={styles.Kicker}>{entity.kicker}</div>
          <div className={styles.Name}>
            {artifact.name}
            {artifact.version != null && <span className={styles.Ver}>v{artifact.version}</span>}
          </div>
        </div>
        <span className={classNames(styles.StateChip, state.cls)}>
          <StateIcon aria-hidden="true" />
          {state.label}
        </span>
      </div>

      <div className={styles.Body}>
        {artifact.summary && <p className={styles.Desc}>{artifact.summary}</p>}

        {artifact.facts && artifact.facts.length > 0 && (
          <div className={styles.Facts}>
            {artifact.facts.map((fact, index) => (
              <div key={index} className={styles.Fact}>
                <span className={styles.FactKey}>{fact.label}</span>
                <span className={classNames(styles.FactValue, { [styles.Mono]: fact.mono })}>
                  {fact.value}
                </span>
              </div>
            ))}
          </div>
        )}

        {artifact.modules && artifact.modules.length > 0 && (
          <div className={styles.Section}>
            <div className={styles.SectionLabel}>Modules in this bundle</div>
            <div className={styles.Refs}>
              {artifact.modules.map((ref, index) => (
                <span key={index} className={styles.Ref}>
                  <Layers aria-hidden="true" />
                  {ref.name}
                  {ref.version != null && <span className={styles.RefVer}>v{ref.version}</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        {artifact.components && artifact.components.length > 0 && (
          <div className={styles.Section}>
            <div className={styles.SectionLabel}>Variable components · inclusion &amp; price</div>
            <div className={styles.Comps}>
              {artifact.components.map((comp, index) => (
                <div
                  key={index}
                  className={classNames(styles.Comp, { [styles.CompMissing]: comp.missing })}
                >
                  <span className={styles.CompName}>
                    <Puzzle aria-hidden="true" />
                    {comp.name}
                  </span>
                  {comp.inclusion && <span className={styles.CompIncl}>{comp.inclusion}</span>}
                  <span className={styles.CompPrice}>
                    {comp.missing ? (
                      <>
                        <TriangleAlert aria-hidden="true" />
                        Needs price
                      </>
                    ) : (
                      comp.price
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {artifact.priceMatrix && (
          <div className={styles.Section}>
            <div className={styles.SectionLabel}>{artifact.priceMatrix.label}</div>
            <PriceMatrix matrix={artifact.priceMatrix.matrix} />
          </div>
        )}

        {artifact.price && (
          <div className={styles.Price}>
            <span className={styles.PriceLabel}>{artifact.price.label}</span>
            <span className={styles.PriceAmount}>
              {artifact.price.amount}
              {artifact.price.sub && <small> {artifact.price.sub}</small>}
            </span>
          </div>
        )}

        {artifact.steps && artifact.steps.length > 0 && (
          <div className={styles.Section}>
            <div className={styles.SectionLabel}>
              {`This performs ${artifact.steps.length} step${artifact.steps.length === 1 ? '' : 's'}, in order`}
            </div>
            <div className={styles.Steps}>
              {artifact.steps.map((step, index) => (
                <div key={index} className={styles.Step}>
                  <span>{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {artifact.gaps && artifact.gaps.length > 0 && (
          <div className={styles.Gap}>
            <div className={styles.GapHead}>
              <TriangleAlert aria-hidden="true" />
              {artifact.gaps.length === 1
                ? '1 gap blocks publish'
                : `${artifact.gaps.length} gaps block publish`}
            </div>
            {artifact.gaps.map((gap, index) => (
              <div key={index} className={styles.GapRow}>
                {gap}
              </div>
            ))}
          </div>
        )}

        {additions}
      </div>

      {footer && <div className={styles.Bar}>{footer}</div>}
    </div>
  );
}
